import { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { Phone, Mail, Check } from 'lucide-react'
import { contactCTAs } from './config'
import type { ChatMessageProps, ContactCTACopy } from '@brochure/shared'

/**
 * Parse `**bold**` markdown into React nodes. Keeps the rest of the text as-is.
 */
function parseMarkdownBold(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return part
  })
}

const ctaIcons: Record<string, React.ReactNode> = {
  phone: <Phone className="w-3 h-3" strokeWidth={2.25} />,
  email: <Mail className="w-3 h-3" strokeWidth={2.25} />,
}

function CopyButton({
  cta,
  label,
  copiedLabel,
}: {
  cta: ContactCTACopy
  label: string
  copiedLabel: string
}) {
  const [copied, setCopied] = useState(false)

  const handleClick = () => {
    navigator.clipboard.writeText(cta.value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-medium tracking-wide uppercase transition-colors border',
        copied
          ? 'text-emerald-600 border-emerald-200 bg-emerald-50'
          : 'text-brand-600 border-brand-200 bg-white hover:bg-brand-50 hover:border-brand-300'
      )}
      aria-label={copied ? copiedLabel : `${label}: ${cta.value}`}
      title={cta.value}
    >
      {copied ? <Check className="w-3 h-3" strokeWidth={2.5} /> : ctaIcons[cta.id]}
      <span>{copied ? copiedLabel : label}</span>
    </button>
  )
}

export function ChatMessage({ message, isTyping = false }: ChatMessageProps) {
  const { t } = useTranslation('common')
  const isUser = message.role === 'user'

  const formattedContent = useMemo(() => {
    if (!message.content) return null
    return parseMarkdownBold(message.content)
  }, [message.content])

  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-md px-3.5 py-2.5',
          isUser
            ? 'bg-brand-500 text-white rounded-br-[2px] shadow-[0_2px_6px_-1px_rgba(74,155,220,0.35)]'
            : 'bg-white text-ink-800 rounded-bl-[2px] border border-ink-200 shadow-sm'
        )}
      >
        {isTyping ? (
          <div className="flex gap-1.5 py-1 px-0.5">
            <span
              className="w-1.5 h-1.5 bg-brand-400 rounded-full animate-bounce"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="w-1.5 h-1.5 bg-brand-400 rounded-full animate-bounce"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="w-1.5 h-1.5 bg-brand-400 rounded-full animate-bounce"
              style={{ animationDelay: '300ms' }}
            />
          </div>
        ) : (
          <p
            className={cn(
              'text-[13.5px] whitespace-pre-wrap leading-[1.55]',
              isUser ? 'text-white' : 'text-ink-800'
            )}
          >
            {formattedContent}
          </p>
        )}
      </div>

      {/* Contact CTAs — only for assistant messages, not typing */}
      {!isUser && !isTyping && message.content && contactCTAs.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2 ml-0.5">
          {contactCTAs.map((cta) =>
            cta.kind === 'copy' ? (
              <CopyButton
                key={cta.id}
                cta={cta}
                label={t(cta.labelKey)}
                copiedLabel={t('chatbot.cta.copied')}
              />
            ) : (
              <a
                key={cta.id}
                href={cta.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-medium tracking-wide uppercase text-brand-600 border border-brand-200 bg-white hover:bg-brand-50 hover:border-brand-300 transition-colors"
              >
                {cta.icon && <img src={cta.icon} alt="" className="w-3 h-3" />}
                <span>{t(cta.labelKey)}</span>
              </a>
            )
          )}
        </div>
      )}
    </div>
  )
}
